import { useCallback, useState, useRef, useEffect } from 'react'

import { GA_CONFIG } from '@features/config/analytics'

export const useGoogleAnalytics = () => {
    const [isLoaded, setIsLoaded] = useState(false)
    const loadingPromiseRef = useRef(null)

    const initializeGA = useCallback((initialData = {}) => {
        if (loadingPromiseRef.current) return loadingPromiseRef.current

        if (window.gtag) {
            setIsLoaded(true)
            return Promise.resolve()
        }

        loadingPromiseRef.current = new Promise((resolve, reject) => {
            window.dataLayer = window.dataLayer || []

            window.gtag = function() {
                window.dataLayer.push(arguments)
            }

            if (GA_CONFIG?.consent) window.gtag('consent', 'default', GA_CONFIG.consent)

            window.gtag('js', new Date())

            const GA_MEASUREMENT_ID = GA_CONFIG?.measurement_id ?? null

            const script = document.createElement('script')
            script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
            script.async = true

            script.onload = () => {
                const checkGtag = setInterval(() => {
                    if (window.gtag) {
                        clearInterval(checkGtag)

                        window.gtag('config', GA_MEASUREMENT_ID, {
                            ...initialData
                        })

                        setIsLoaded(true)
                        resolve()
                    }
                }, 100)

                setTimeout(() => {
                    clearInterval(checkGtag)
                    if (!window.gtag) reject(new Error('Google Analytics failed to load after 10 seconds'))
                }, 10000)
            }

            script.onerror = () => {
                reject(new Error('Failed to load Google Analytics script'))
            }

            document.head.appendChild(script)
        })

        return loadingPromiseRef.current
    }, [])

    const trackPageView = useCallback(async (data) => {
        if (!isLoaded) await initializeGA()

        if (window.gtag) {
            window.gtag('event', 'page_view', {
                // eslint-disable-next-line
                page_title: data.page_name || document.title,
                // eslint-disable-next-line
                page_location: data.page_url || window.location.href,
                // eslint-disable-next-line
                page_path: window.location.hash || window.location.pathname,
                ...data
            })
        }
    }, [isLoaded, initializeGA])

    const trackEvent = useCallback(async (eventName, data = {}) => {
        if (!isLoaded) await initializeGA()

        if (window.gtag) window.gtag('event', eventName, data)
    }, [isLoaded, initializeGA])

    const updateConsent = useCallback((consentUpdates) => {
        if (window.gtag) window.gtag('consent', 'update', consentUpdates)
    }, [])

    return { initializeGA, trackPageView, trackEvent, updateConsent, isLoaded }
}

export const usePageTracking = (trackingData) => {
    const { trackPageView, trackEvent } = useGoogleAnalytics()
    const hasTrackedRef = useRef(false)

    useEffect(() => {
        if (!hasTrackedRef.current && trackingData) {
            hasTrackedRef.current = true
            trackPageView(trackingData)
            trackEvent('dashboard_page', trackingData)
        }
    }, [])
}